import { useEffect, useState } from 'react';
import type {
  SupportTicketInfo,
  SupportTicketMessageInfo,
  SupportTicketStatus,
} from '@shared/types';
import { formatRelative } from '@shared/format';
import { useAuthStore } from '../app/authStore';
import { api, ApiError, describeError } from '../lib/api';
import { Badge, Button, Card, CardHeader, EmptyState, PageHeader, Skeleton } from '../components/ui';
import { Field, Input, Notice, Select, Textarea } from '../components/form';

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: '处理中',
  pending: '等待我们回复',
  resolved: '已解决',
  closed: '已关闭',
};

const STATUS_TONES: Record<SupportTicketStatus, 'brand' | 'info' | 'ok' | 'neutral'> = {
  open: 'brand',
  pending: 'info',
  resolved: 'ok',
  closed: 'neutral',
};

const CATEGORY_LABELS = {
  billing: '计费与订单',
  technical: '技术问题',
  account: '账号与权限',
  other: '其他',
} as const;

type TicketCategory = keyof typeof CATEGORY_LABELS;

interface TicketDetailResponse {
  ticket: SupportTicketInfo;
  messages: SupportTicketMessageInfo[];
}

/** 工单与支持：用户侧列表、新建、详情与回复。 */
export function SupportPage() {
  const can = useAuthStore((state) => state.can('support.create'));

  const [items, setItems] = useState<SupportTicketInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory>('billing');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [detail, setDetail] = useState<TicketDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      const result = await api.get<{ items: SupportTicketInfo[] }>('/api/support/tickets');
      setItems(result.items);
    } catch (error) {
      setListError(error instanceof ApiError ? error.message : describeError(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function openTicket(ticket: SupportTicketInfo) {
    setDetailLoading(true);
    setReplyError(null);
    try {
      setDetail(await api.get<TicketDetailResponse>(`/api/support/tickets/${ticket.id}`));
      setReply('');
    } catch (error) {
      setReplyError(error instanceof ApiError ? error.message : describeError(error));
    } finally {
      setDetailLoading(false);
    }
  }

  async function submitTicket() {
    setCreating(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const result = await api.post<TicketDetailResponse>('/api/support/tickets', {
        subject: subject.trim(),
        category,
        priority,
        body: body.trim(),
      });
      setSubject('');
      setBody('');
      setDetail(result);
      await load();
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        setFormError(error.message);
      } else {
        setFormError(describeError(error));
      }
    } finally {
      setCreating(false);
    }
  }

  async function submitReply() {
    if (!detail) return;
    setReplying(true);
    setReplyError(null);
    try {
      const result = await api.post<TicketDetailResponse>(
        `/api/support/tickets/${detail.ticket.id}/messages`,
        { body: reply.trim() },
      );
      setDetail(result);
      setReply('');
      await load();
    } catch (error) {
      setReplyError(error instanceof ApiError ? error.message : describeError(error));
    } finally {
      setReplying(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="工单与支持"
        description="提交后会进入我们的运营队列，回复会同步到这里的会话与你的邮箱。"
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-5">
          <Card>
            <CardHeader title="我的工单" hint={`${items.length} 条`} />
            {loading ? (
              <Skeleton className="h-24 w-full" />
            ) : listError ? (
              <Notice tone="danger">{listError}</Notice>
            ) : items.length === 0 ? (
              <EmptyState
                icon="help"
                title="还没有工单"
                description="遇到计费、账号或接入问题，可以在右侧提交。"
              />
            ) : (
              <ul className="divide-y divide-line">
                {items.map((ticket) => (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      onClick={() => void openTicket(ticket)}
                      className="flex w-full items-center gap-3 py-3 text-left hover:bg-nest-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-fg">{ticket.subject}</p>
                        <p className="mt-0.5 text-xs text-fg-muted">
                          {CATEGORY_LABELS[ticket.category]} · {formatRelative(ticket.updatedAt)} ·{' '}
                          {ticket.messageCount} 条消息
                        </p>
                      </div>
                      <Badge tone={STATUS_TONES[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {detailLoading ? <Skeleton className="h-40 w-full" /> : null}

          {detail ? (
            <Card>
              <CardHeader
                title={detail.ticket.subject}
                hint={`${CATEGORY_LABELS[detail.ticket.category]} · ${STATUS_LABELS[detail.ticket.status]}`}
              />
              <div className="space-y-3">
                {detail.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`rounded-control px-4 py-3 text-[13px] ${
                      message.authorType === 'user' ? 'bg-nest-2' : 'bg-brand-soft'
                    }`}
                  >
                    <p className="mb-1 flex items-center justify-between text-xs text-fg-muted">
                      <span>
                        {message.authorName}
                        {message.authorType === 'admin' ? '（Havorix 支持）' : ''}
                      </span>
                      <span>{formatRelative(message.createdAt)}</span>
                    </p>
                    <p className="whitespace-pre-wrap text-fg">{message.body}</p>
                  </div>
                ))}
              </div>

              {detail.ticket.status === 'closed' ? (
                <div className="mt-4">
                  <Notice tone="info">该工单已关闭。如需继续沟通，请新建一条工单。</Notice>
                </div>
              ) : can ? (
                <div className="mt-4 space-y-2">
                  {replyError ? <Notice tone="danger">{replyError}</Notice> : null}
                  <Textarea
                    value={reply}
                    onChange={setReply}
                    placeholder="补充说明或回复支持人员…"
                    rows={3}
                    maxLength={5000}
                  />
                  <Button variant="primary" loading={replying} onClick={submitReply}>
                    发送回复
                  </Button>
                </div>
              ) : null}
            </Card>
          ) : null}
        </div>

        <Card>
          <CardHeader title="新建工单" hint={can ? '描述越具体，处理越快' : '当前角色只能查看'} />
          {!can ? (
            <Notice tone="info">你的角色没有提交工单的权限，请联系组织管理员。</Notice>
          ) : (
            <div className="space-y-3">
              {formError ? <Notice tone="danger">{formError}</Notice> : null}
              <Field label="标题" htmlFor="ticket-subject" error={fieldErrors.subject} required>
                <Input
                  id="ticket-subject"
                  value={subject}
                  onChange={setSubject}
                  placeholder="一句话说明问题"
                  maxLength={80}
                />
              </Field>
              <Field label="问题类型" htmlFor="ticket-category">
                <Select
                  id="ticket-category"
                  value={category}
                  onChange={(value) => setCategory(value as TicketCategory)}
                  options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
                    value: value as TicketCategory,
                    label,
                  }))}
                />
              </Field>
              <Field label="优先级" htmlFor="ticket-priority">
                <Select
                  id="ticket-priority"
                  value={priority}
                  onChange={setPriority}
                  options={[
                    { value: 'low', label: '低' },
                    { value: 'normal', label: '普通' },
                    { value: 'high', label: '高' },
                  ]}
                />
              </Field>
              <Field label="问题描述" htmlFor="ticket-body" error={fieldErrors.body} required>
                <Textarea
                  id="ticket-body"
                  value={body}
                  onChange={setBody}
                  placeholder="操作步骤、报错信息、发生时间"
                  rows={6}
                  maxLength={5000}
                />
              </Field>
              <Button variant="primary" block loading={creating} onClick={submitTicket}>
                提交工单
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

